import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-2'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '🔷',
    category: '내각의 합 공식',
    learnStepId: 'GB01',
    question: '오각형의 내각의 합은 몇 도인가요?',
    correctAnswer: '(5-2)×180° = 3×180° = 540°',
    wrongAnswer: '5×180° = 900°',
    misconceptionCode: 'GP01',
    note: 'n각형 내각의 합 공식에서 (n-2)를 빠뜨림',
    analysis: {
      diagnosis: '학생이 "5×180°=900°"라고 답했습니다. 삼각형의 분할 개념 없이 꼭짓점 수에 바로 180°를 곱했습니다.',
      rootCause: 'n각형을 삼각형으로 분할하면 (n-2)개의 삼각형이 생깁니다. n개가 아니라 (n-2)개입니다!',
      correction: '올바른 공식:\n① n각형 → (n-2)개의 삼각형으로 분할\n② 각 삼각형의 내각 합 = 180°\n③ 내각의 합 = (n-2) × 180°\n오각형: (5-2)×180° = 3×180° = 540°',
      example: '삼각형(n=3): (3-2)×180°=180° ✓\n사각형(n=4): (4-2)×180°=360° ✓\n오각형(n=5): (5-2)×180°=540° ✓',
    },
    followUps: [
      { q: '정육각형의 내각의 합과 한 내각의 크기는?', a: '(6-2)×180°=720°, 한 내각=720°÷6=120°' },
      { q: '외각의 합이 360°인 이유는 무엇인가요?', a: '다각형을 한 바퀴 돌면 360° 회전하기 때문에 외각의 합은 항상 360°입니다.' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '📐',
    category: '이등변삼각형 성질',
    learnStepId: 'GB02',
    question: '이등변삼각형의 꼭짓각이 40°일 때 밑각의 크기는?',
    correctAnswer: '(180°-40°)÷2 = 70°',
    wrongAnswer: '180°-40° = 140°',
    misconceptionCode: 'GP02',
    note: '두 밑각이 같다는 사실을 간과하고 나누지 않음',
    analysis: {
      diagnosis: '학생이 "180°-40°=140°"라고 답했습니다. 남은 140°를 두 밑각으로 나누어야 한다는 점을 놓쳤습니다.',
      rootCause: '이등변삼각형에서 두 밑각의 크기는 서로 같습니다. 남은 각도를 반드시 2로 나눠야 합니다.',
      correction: '① 삼각형 내각의 합 = 180°\n② 이등변삼각형: 두 밑각의 크기 같음\n③ 두 밑각의 합 = 180° - 40° = 140°\n④ 각 밑각 = 140° ÷ 2 = 70°',
      example: '꼭짓각=60°: 밑각=(180-60)÷2=60° (정삼각형!)\n꼭짓각=100°: 밑각=(180-100)÷2=40°\n꼭짓각=40°: 밑각=(180-40)÷2=70° ✓',
    },
    followUps: [
      { q: '이등변삼각형의 두 밑각이 각각 55°일 때 꼭짓각은?', a: '180°-55°-55°=70°. 꼭짓각=70°' },
      { q: '정삼각형은 이등변삼각형인가요?', a: '네! 정삼각형은 세 변이 같으므로 이등변삼각형의 특수한 경우입니다. 세 각 모두 60°입니다.' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '🥧',
    category: '부채꼴 호 길이',
    learnStepId: 'GB03',
    question: '반지름 6cm, 중심각 90°인 부채꼴의 호 길이는?',
    correctAnswer: '2π×6×(90/360) = 3π cm',
    wrongAnswer: '6π cm (원 둘레의 반)',
    misconceptionCode: 'GP03',
    note: '중심각 90°=¼원 임을 놓치고 ½로 계산함',
    analysis: {
      diagnosis: '학생이 "6π cm"라고 답했습니다. 90°=원의 ¼임을 인식하지 못하고 원 둘레의 절반(180°)으로 계산했습니다.',
      rootCause: '호의 길이 공식: l = 2πr × (θ/360°). 90°는 360°의 ¼이므로 전체 둘레의 ¼입니다.',
      correction: '① 원 전체 둘레 = 2π×6 = 12π cm\n② 90°는 360°의 90/360 = 1/4\n③ 호의 길이 = 12π × (1/4) = 3π cm\n공식: l = 2π×6×(90÷360) = 3π cm ✓',
      example: 'r=6, θ=180°: l=2π×6×½=6π cm\nr=6, θ=90°: l=2π×6×¼=3π cm\nr=6, θ=60°: l=2π×6×⅙=2π cm',
    },
    followUps: [
      { q: '반지름 9cm, 중심각 120°인 부채꼴의 호 길이는?', a: '2π×9×(120/360)=18π×⅓=6π cm' },
      { q: '부채꼴의 둘레를 구하려면 호 길이 외에 무엇을 더해야 하나요?', a: '반지름 2개를 더해야 합니다. 둘레=호 길이+2r' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '⭕',
    category: '원의 넓이 공식',
    learnStepId: 'GB04',
    question: '반지름 4cm인 원의 넓이는?',
    correctAnswer: 'π×4² = 16π cm²',
    wrongAnswer: '2π×4 = 8π cm² (둘레 공식 사용)',
    misconceptionCode: 'GP04',
    note: '넓이(πr²)와 둘레(2πr) 공식을 혼동',
    analysis: {
      diagnosis: '학생이 "8π cm²"라고 답했습니다. 원의 둘레 공식(2πr)을 원의 넓이로 잘못 사용했습니다.',
      rootCause: '원의 공식 두 가지를 구분해야 합니다:\n• 둘레(원주): 2πr (선의 길이 → cm)\n• 넓이: πr² (면의 넓이 → cm²)',
      correction: '① 둘레(C) = 2πr = 2π×4 = 8π cm\n② 넓이(A) = πr² = π×4² = π×16 = 16π cm²\n단위 확인: 넓이는 cm² (제곱 단위)!',
      example: 'r=3: 둘레=6π cm, 넓이=9π cm²\nr=4: 둘레=8π cm, 넓이=16π cm²\nr=5: 둘레=10π cm, 넓이=25π cm²',
    },
    followUps: [
      { q: '넓이가 25π cm²인 원의 반지름은?', a: 'πr²=25π → r²=25 → r=5 cm' },
      { q: '반지름이 3배가 되면 넓이는 몇 배가 되나요?', a: '9배! 넓이=πr²이므로 반지름이 3배→r²은 9배→넓이 9배' },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '🎯',
    category: '부채꼴 넓이 공식',
    learnStepId: 'GB03',
    question: '호 길이가 4π cm이고 반지름이 6cm인 부채꼴의 넓이는?',
    correctAnswer: 'S = ½lr = ½×4π×6 = 12π cm²',
    wrongAnswer: '4π×6 = 24π cm² (곱셈만 함)',
    misconceptionCode: 'GP03',
    note: 'S=½lr 공식에서 ½를 빠뜨림',
    analysis: {
      diagnosis: '학생이 "24π cm²"라고 답했습니다. 부채꼴 넓이 공식 S=½lr에서 ½를 곱하지 않았습니다.',
      rootCause: 'S=½lr 공식에서 ½는 매우 중요합니다. 삼각형 넓이=½×밑변×높이처럼, 부채꼴도 ½가 필요합니다.',
      correction: '부채꼴 넓이 두 가지 공식:\n① S = πr²×(θ/360°)\n② S = ½lr (l=호의 길이)\n\n계산: S = ½×4π×6 = 12π cm² ✓',
      example: 'l=6π, r=6: S=½×6π×6=18π cm²\nl=4π, r=6: S=½×4π×6=12π cm²\nl=3π, r=4: S=½×3π×4=6π cm²',
    },
    followUps: [
      { q: '반지름 8cm, 호 길이 5π cm인 부채꼴의 넓이는?', a: 'S=½×5π×8=20π cm²' },
      { q: '넓이 18π cm², 반지름 6cm인 부채꼴의 호 길이는?', a: '18π=½×l×6 → 18π=3l → l=6π cm' },
    ],
  },
]

// ── 하위 컴포넌트 ─────────────────────────────────────────────────────────────
function FragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const visible = fragments.slice(0, 2)
  if (visible.length === 0) return null
  return (
    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={11} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-1.5 rounded-lg border text-xs ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <span className="font-semibold">{f.fragment.title}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FollowUpCard({ fup, idx }: { fup: { q: string; a: string }; idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-emerald-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left">
        <span className="text-sm font-medium text-gray-800">추가 질문 {idx + 1}: {fup.q}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="p-3 bg-white flex items-start gap-2">
          <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-700">{fup.a}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
type Step = 'question' | 'wrong' | 'analysis' | 'correct'

export default function G1_2TutorPage() {
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)

  const [scenarioIdx, setScenarioIdx] = useState(0)
  const [step, setStep] = useState<Step>('question')
  const scenario = DEMO_SCENARIOS[scenarioIdx]

  const reset = (idx: number) => {
    setScenarioIdx(idx)
    setStep('question')
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      {/* 헤더 */}
      <div className="text-center py-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
        <div className="text-3xl mb-1">🔷</div>
        <h1 className="text-xl font-bold text-gray-800">평면도형 AI 튜터</h1>
        <p className="text-gray-500 text-xs mt-1">학생 오개념을 AI가 진단하고 교정합니다</p>
      </div>

      <FragmentPanel fragments={predictedFragments} />

      {/* 시나리오 선택 */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {DEMO_SCENARIOS.map((sc, i) => (
          <button key={sc.id} onClick={() => reset(i)}
            className={`flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2 rounded-xl border-2 transition-all ${
              scenarioIdx === i
                ? 'border-emerald-400 bg-emerald-50 shadow-sm'
                : 'border-gray-200 bg-white hover:border-emerald-200'
            }`}>
            <span className="text-xl">{sc.emoji}</span>
            <span className="text-xs font-medium text-gray-700">{sc.label}</span>
            <span className="text-xs text-gray-400">{sc.category}</span>
          </button>
        ))}
      </div>

      {/* 시나리오 카드 */}
      <div className="rounded-2xl border-2 border-emerald-200 overflow-hidden shadow-sm">
        {/* 카테고리 헤더 */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 flex items-center gap-3">
          <span className="text-2xl">{scenario.emoji}</span>
          <div>
            <p className="text-white font-bold">{scenario.category}</p>
            <p className="text-white/70 text-xs">{scenario.misconceptionCode} · 개념 {scenario.learnStepId}</p>
          </div>
        </div>

        <div className="p-5 space-y-4 bg-white">
          {/* 문제 */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
              <Target size={12} /> 질문
            </p>
            <p className="text-gray-800 font-medium">{scenario.question}</p>
          </div>

          {/* STEP 1: 문제 → 학생 오답 표시 */}
          {step === 'question' && (
            <button onClick={() => setStep('wrong')}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
              <MessageCircle size={16} /> 학생 답변 보기
            </button>
          )}

          {/* STEP 2: 오답 표시 → 분석 */}
          {(step === 'wrong' || step === 'analysis' || step === 'correct') && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-bold text-red-600 mb-1 flex items-center gap-1">
                <XCircle size={12} /> 학생 오답
              </p>
              <p className="text-gray-800 font-mono">{scenario.wrongAnswer}</p>
              <p className="text-xs text-red-500 mt-2 flex items-start gap-1">
                <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                오개념: {scenario.note}
              </p>
            </div>
          )}

          {step === 'wrong' && (
            <button onClick={() => setStep('analysis')}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
              <Brain size={16} /> AI 오개념 분석
            </button>
          )}

          {/* STEP 3: 분석 결과 */}
          {(step === 'analysis' || step === 'correct') && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
                <Brain size={12} /> AI 분석 결과
              </p>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">📋 진단</p>
                <p className="text-sm text-gray-700">{scenario.analysis.diagnosis}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">🔍 근본 원인</p>
                <p className="text-sm text-gray-700">{scenario.analysis.rootCause}</p>
              </div>
            </div>
          )}

          {step === 'analysis' && (
            <button onClick={() => setStep('correct')}
              className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
              <CheckCircle size={16} /> 올바른 풀이 보기
            </button>
          )}

          {/* STEP 4: 정답 + 예시 + 추가 질문 */}
          {step === 'correct' && (
            <>
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                  <CheckCircle size={12} /> 올바른 풀이
                </p>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                  {scenario.analysis.correction}
                </pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1">
                  <BookOpen size={12} /> 유사 예제
                </p>
                <pre className="text-sm text-blue-800 whitespace-pre-wrap font-sans">
                  {scenario.analysis.example}
                </pre>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-600 flex items-center gap-1">
                  <Sparkles size={12} /> 추가 연습 질문
                </p>
                {scenario.followUps.map((fup, i) => (
                  <FollowUpCard key={i} fup={fup} idx={i} />
                ))}
              </div>

              <button onClick={() => reset(scenarioIdx)}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
                <RotateCcw size={16} /> 다시 풀기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
